import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Trash2,
  Pencil,
  Save,
  RefreshCw,
  AlertTriangle,
  ClipboardList,
  Network,
  MessageSquare,
  X,
  UserPlus,
  Search,
} from 'lucide-react';

import {
  fetchTasksThunk,
  createTaskThunk,
  updateTaskThunk,
  updateTaskStatusThunk,
  deleteTaskThunk,
  fetchTaskCommentsThunk,
  addTaskCommentThunk,
} from '../../../redux/admin/tasksActionThunks';
import {
  fetchOrgRolesThunk,
  createOrgRoleThunk,
  updateOrgRoleThunk,
  deleteOrgRoleThunk,
  fetchOrgRoleHoldersThunk,
  mapUserToOrgRoleThunk,
  unmapUserFromOrgRoleThunk,
} from '../../../redux/admin/orgRolesActionThunks';
import AdminAPI from '../../../redux/admin/adminApi';

const PRIORITIES = ['critical', 'high', 'medium', 'low'];
const STATUSES = ['todo', 'in_progress', 'blocked', 'done', 'cancelled'];
const CATEGORIES = ['executive', 'development', 'governance_growth'];

const PRIORITY_COLORS = {
  critical: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30' },
  high: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/30' },
  medium: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30' },
  low: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/30' },
};

/**
 * Company Ops — Task Delegation (tasks tagged with a company org-role,
 * assignable to one or more people who occupy that chart, with status/
 * comments) + Org Chart (the roles themselves and who holds them). Full
 * admins reach this tab alongside every other admin tab; non-admin staff
 * (anyone mapped into user_org_roles) only ever see this one tab -- see
 * GET /admin/access and adminDashboard.jsx's tab filtering.
 */
const AdminCompanyOpsComponent = ({ darkMode = true, initialSubView = null, initialTaskId = null }) => {
  const { t } = useTranslation();
  const [activeView, setActiveView] = useState(initialSubView === 'orgChart' ? 'orgChart' : 'tasks');

  const themeClasses = {
    cardBg: darkMode ? 'bg-gray-800' : 'bg-white',
    text: darkMode ? 'text-white' : 'text-gray-900',
    textSecondary: darkMode ? 'text-gray-300' : 'text-gray-600',
    textMuted: darkMode ? 'text-gray-400' : 'text-gray-500',
    border: darkMode ? 'border-gray-700' : 'border-gray-200',
    hoverBg: darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50',
    inputBg: darkMode ? 'bg-gray-700' : 'bg-white',
    inputBorder: darkMode ? 'border-gray-600' : 'border-gray-300',
    buttonPrimary: 'bg-blue-600 hover:bg-blue-700 text-white',
    buttonSecondary: darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700',
  };

  const views = [
    { key: 'tasks', label: t('admin.companyOps.views.tasks'), icon: <ClipboardList className="h-4 w-4 mr-2" /> },
    { key: 'orgChart', label: t('admin.companyOps.views.orgChart'), icon: <Network className="h-4 w-4 mr-2" /> },
  ];

  return (
    <div className="space-y-6">
      <div className={`flex space-x-1 border-b ${themeClasses.border}`}>
        {views.map((v) => (
          <button
            key={v.key}
            onClick={() => setActiveView(v.key)}
            className={`flex items-center px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
              activeView === v.key
                ? `border-blue-600 ${darkMode ? 'text-blue-400 bg-gray-800' : 'text-blue-600 bg-white'}`
                : `border-transparent ${themeClasses.textMuted} ${themeClasses.hoverBg}`
            }`}
          >
            {v.icon}
            {v.label}
          </button>
        ))}
      </div>

      {activeView === 'tasks' && <TasksPanel themeClasses={themeClasses} darkMode={darkMode} focusTaskId={initialTaskId} />}
      {activeView === 'orgChart' && <OrgChartPanel themeClasses={themeClasses} darkMode={darkMode} />}
    </div>
  );
};

/* ============================== Tasks ============================== */

const emptyTaskForm = {
  title: '', description: '', org_role_id: '', priority: 'medium', due_date: '', assignee_ids: [],
};

export const TasksPanel = ({ themeClasses, darkMode, sourceType, sourceId, focusTaskId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUser = useSelector((s) => s.auth?.user);
  const isAdmin = currentUser?.is_admin === true || currentUser?.role === 'admin';

  const items = useSelector((s) => s.tasks.items);
  const loading = useSelector((s) => s.tasks.loading.items);
  const error = useSelector((s) => s.tasks.errors.items);
  // create/update/delete/status-change/comment all had no disabled guard at
  // all here, and mutate errors were captured in the slice but never
  // rendered -- only the list-fetch error above ever showed anything.
  // Mirrors the identical fix already applied to OrgRolesPanel above.
  const mutating = useSelector((s) => s.tasks.loading.mutate);
  const mutateError = useSelector((s) => s.tasks.errors.mutate);
  const commentsByTask = useSelector((s) => s.tasks.commentsByTask);
  const orgRoles = useSelector((s) => s.orgRoles.items);
  const holdersByRole = useSelector((s) => s.orgRoles.holdersByRole);

  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [orgRoleFilter, setOrgRoleFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newTask, setNewTask] = useState(emptyTaskForm);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyTaskForm);
  const [expandedId, setExpandedId] = useState(null);
  const [commentDraft, setCommentDraft] = useState('');

  // "Who can I delegate to" -- assignable people are anyone who occupies at
  // least one company org role (see UserOrgRole), not an open-ended user
  // search, since staff-only viewers can't call the full admin user list.
  const assignableUsers = useMemo(() => {
    const map = new Map();
    Object.values(holdersByRole).forEach((holders) => {
      (holders || []).forEach((h) => map.set(h.user_id, h));
    });
    return Array.from(map.values());
  }, [holdersByRole]);

  useEffect(() => {
    if (orgRoles.length === 0) dispatch(fetchOrgRolesThunk());
  }, [dispatch, orgRoles.length]);

  useEffect(() => {
    orgRoles.forEach((role) => {
      if (!holdersByRole[role.id]) dispatch(fetchOrgRoleHoldersThunk(role.id));
    });
  }, [dispatch, orgRoles, holdersByRole]);

  useEffect(() => {
    dispatch(fetchTasksThunk({
      status: statusFilter || undefined,
      priority: priorityFilter || undefined,
      org_role_id: orgRoleFilter || undefined,
      source_type: sourceType || undefined,
      source_id: sourceId || undefined,
    }));
  }, [dispatch, statusFilter, priorityFilter, orgRoleFilter, sourceType, sourceId]);

  // Deep-link from a task notification (see notificationCenterPage.jsx /
  // notificationsDropDown.js) -- once the list has loaded, auto-expand the
  // task the notification pointed at so its comment thread is visible.
  useEffect(() => {
    if (focusTaskId && items.some((task) => task.id === focusTaskId) && expandedId !== focusTaskId) {
      setExpandedId(focusTaskId);
      if (!commentsByTask[focusTaskId]) dispatch(fetchTaskCommentsThunk(focusTaskId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTaskId, items]);

  const canManage = (task) => isAdmin || task.created_by === currentUser?.id;
  const canAct = (task) => canManage(task) || (task.assignees || []).some((a) => a.user_id === currentUser?.id);

  const roleTitle = (id) => orgRoles.find((r) => r.id === id)?.title;

  const toPayload = (form) => ({
    title: form.title,
    description: form.description || null,
    org_role_id: form.org_role_id ? Number(form.org_role_id) : null,
    priority: form.priority,
    due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
    assignee_ids: form.assignee_ids,
  });

  const handleCreate = (e) => {
    e.preventDefault();
    if (!newTask.title.trim()) return;
    const payload = toPayload(newTask);
    if (sourceType) { payload.source_type = sourceType; payload.source_id = sourceId; }
    dispatch(createTaskThunk(payload)).then((res) => {
      if (!res.error) {
        setNewTask(emptyTaskForm);
        setShowCreate(false);
      }
    });
  };

  const startEdit = (task) => {
    setEditingId(task.id);
    setEditForm({
      title: task.title,
      description: task.description || '',
      org_role_id: task.org_role_id || '',
      priority: task.priority,
      due_date: task.due_date ? task.due_date.slice(0, 10) : '',
      assignee_ids: (task.assignees || []).map((a) => a.user_id),
    });
  };

  const saveEdit = (taskId) => {
    dispatch(updateTaskThunk({ taskId, updates: toPayload(editForm) })).then((res) => {
      if (!res.error) setEditingId(null);
    });
  };

  const handleStatusChange = (task, status) => {
    dispatch(updateTaskStatusThunk({ taskId: task.id, status }));
  };

  const handleDelete = (taskId) => {
    if (window.confirm(t('admin.companyOps.tasks.deleteConfirm'))) {
      dispatch(deleteTaskThunk(taskId));
    }
  };

  const toggleExpand = (taskId) => {
    if (expandedId === taskId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(taskId);
    setCommentDraft('');
    if (!commentsByTask[taskId]) dispatch(fetchTaskCommentsThunk(taskId));
  };

  const handleAddComment = (taskId) => {
    if (!commentDraft.trim()) return;
    dispatch(addTaskCommentThunk({ taskId, body: commentDraft.trim() })).then((res) => {
      if (!res.error) setCommentDraft('');
    });
  };

  const toggleAssignee = (form, setForm, userId) => {
    setForm((f) => ({
      ...f,
      assignee_ids: f.assignee_ids.includes(userId)
        ? f.assignee_ids.filter((id) => id !== userId)
        : [...f.assignee_ids, userId],
    }));
  };

  const renderAssigneePicker = (form, setForm) => (
    <div className="flex flex-wrap gap-2">
      {assignableUsers.length === 0 && (
        <p className={`text-xs ${themeClasses.textMuted}`}>{t('admin.companyOps.tasks.noAssignableUsers')}</p>
      )}
      {assignableUsers.map((u) => {
        const selected = form.assignee_ids.includes(u.user_id);
        return (
          <button
            type="button"
            key={u.user_id}
            onClick={() => toggleAssignee(form, setForm, u.user_id)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
              selected
                ? 'bg-blue-600 border-blue-600 text-white'
                : `${themeClasses.inputBorder} ${themeClasses.textSecondary} ${themeClasses.hoverBg}`
            }`}
          >
            {u.username}
          </button>
        );
      })}
    </div>
  );

  const taskForm = (form, setForm, onSubmit, onCancel, submitLabel, disabled = false) => (
    <form onSubmit={onSubmit} className={`mb-4 p-4 rounded-lg border ${themeClasses.border} space-y-3`}>
      <input
        type="text"
        placeholder={t('admin.companyOps.tasks.titlePlaceholder')}
        value={form.title}
        onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
        className={`w-full px-3 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`}
        required
      />
      <textarea
        placeholder={t('admin.companyOps.tasks.descriptionPlaceholder')}
        value={form.description}
        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
        className={`w-full px-3 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm resize-none`}
        rows={2}
      />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <select value={form.org_role_id} onChange={(e) => setForm((f) => ({ ...f, org_role_id: e.target.value }))}
          className={`px-2.5 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`}>
          <option value="">{t('admin.companyOps.tasks.noOrgRole')}</option>
          {orgRoles.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
        </select>
        <select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
          className={`px-2.5 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`}>
          {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <input type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))}
          className={`px-2.5 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`} />
      </div>
      <div>
        <p className={`text-xs font-medium mb-1.5 ${themeClasses.textMuted}`}>{t('admin.companyOps.tasks.assignTo')}</p>
        {renderAssigneePicker(form, setForm)}
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={disabled} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${themeClasses.buttonPrimary}`}>
          <Save className="h-4 w-4" /> {submitLabel}
        </button>
        <button type="button" onClick={onCancel} disabled={disabled} className={`px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${themeClasses.buttonSecondary}`}>
          {t('admin.companyOps.tasks.cancel')}
        </button>
      </div>
    </form>
  );

  return (
    <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 shadow-sm`}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className={`text-lg font-semibold ${themeClasses.text}`}>{t('admin.companyOps.tasks.heading')}</h2>
          <p className={`text-sm ${themeClasses.textMuted} mt-0.5`}>{t('admin.companyOps.tasks.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className={`px-2.5 py-1.5 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`}>
            <option value="">{t('admin.companyOps.tasks.allStatuses')}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </select>
          <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}
            className={`px-2.5 py-1.5 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`}>
            <option value="">{t('admin.companyOps.tasks.allPriorities')}</option>
            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={orgRoleFilter} onChange={(e) => setOrgRoleFilter(e.target.value)}
            className={`px-2.5 py-1.5 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`}>
            <option value="">{t('admin.companyOps.tasks.allRoles')}</option>
            {orgRoles.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
          </select>
          {(loading || mutating) && <RefreshCw className={`h-4 w-4 animate-spin ${themeClasses.textMuted}`} />}
          <button onClick={() => setShowCreate((s) => !s)} disabled={mutating}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${themeClasses.buttonPrimary}`}>
            <Plus className="h-4 w-4" /> {t('admin.companyOps.tasks.newTask')}
          </button>
        </div>
      </div>

      {error && (
        <div className={`mb-4 flex items-center gap-2 p-3 rounded-lg ${darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700'}`}>
          <AlertTriangle className="h-4 w-4 flex-shrink-0" /> {error}
        </div>
      )}
      {mutateError && (
        <div className={`mb-4 flex items-center gap-2 p-3 rounded-lg ${darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700'}`}>
          <AlertTriangle className="h-4 w-4 flex-shrink-0" /> {mutateError}
        </div>
      )}

      {showCreate && taskForm(newTask, setNewTask, handleCreate, () => setShowCreate(false), t('admin.companyOps.tasks.create'), mutating)}

      <div className="space-y-2">
        {items.length === 0 && !loading && (
          <p className={`text-sm ${themeClasses.textMuted} py-4 text-center`}>{t('admin.companyOps.tasks.noTasksMatch')}</p>
        )}
        {items.map((task) => {
          const colors = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium;
          const isEditing = editingId === task.id;
          const isExpanded = expandedId === task.id;
          const comments = commentsByTask[task.id];

          if (isEditing) {
            return (
              <div key={task.id} className={`p-4 rounded-lg border ${themeClasses.border}`}>
                {taskForm(editForm, setEditForm, (e) => { e.preventDefault(); saveEdit(task.id); }, () => setEditingId(null), t('admin.companyOps.tasks.save'), mutating)}
              </div>
            );
          }

          return (
            <div key={task.id} className={`p-4 rounded-lg border ${themeClasses.border} ${themeClasses.hoverBg} transition-colors`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full border ${colors.bg} ${colors.text} ${colors.border}`}>
                      {task.priority}
                    </span>
                    {task.org_role_id && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                        {roleTitle(task.org_role_id) || task.org_role_id}
                      </span>
                    )}
                    <span className={`font-medium ${themeClasses.text}`}>{task.title}</span>
                  </div>
                  {task.description && <p className={`text-sm ${themeClasses.textSecondary} mt-1`}>{task.description}</p>}
                  <div className={`flex items-center gap-3 mt-2 text-xs ${themeClasses.textMuted} flex-wrap`}>
                    {task.due_date && <span>{t('admin.companyOps.tasks.due')}: {new Date(task.due_date).toLocaleDateString()}</span>}
                    {task.created_by_username && <span>{t('admin.companyOps.tasks.by')}: {task.created_by_username}</span>}
                    {(task.assignees || []).length > 0 && (
                      <span>{t('admin.companyOps.tasks.assignees')}: {task.assignees.map((a) => a.username).join(', ')}</span>
                    )}
                    <button onClick={() => toggleExpand(task.id)} className="flex items-center gap-1 hover:underline">
                      <MessageSquare className="h-3.5 w-3.5" /> {task.comment_count || 0}
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <select
                    value={task.status}
                    onChange={(e) => handleStatusChange(task, e.target.value)}
                    disabled={!canAct(task) || mutating}
                    className={`px-2 py-1 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-xs disabled:opacity-50`}
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                  </select>
                  {canManage(task) && (
                    <>
                      <button onClick={() => startEdit(task)} disabled={mutating} aria-label={t('admin.companyOps.tasks.editAria', { title: task.title })}
                        className={`p-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${darkMode ? 'text-gray-500 hover:text-blue-400 hover:bg-blue-900/20' : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'}`}>
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(task.id)} disabled={mutating} aria-label={t('admin.companyOps.tasks.deleteAria', { title: task.title })}
                        className={`p-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${darkMode ? 'text-gray-500 hover:text-red-400 hover:bg-red-900/20' : 'text-gray-400 hover:text-red-600 hover:bg-red-50'}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {isExpanded && (
                <div className={`mt-3 pt-3 border-t ${themeClasses.border} space-y-2`}>
                  {(comments?.items || []).map((c) => (
                    <div key={c.id} className={`text-sm p-2 rounded-lg ${darkMode ? 'bg-gray-900/40' : 'bg-gray-50'}`}>
                      <span className={`font-medium ${themeClasses.text}`}>{c.author_username || t('admin.companyOps.tasks.unknownUser')}</span>{' '}
                      <span className={themeClasses.textMuted}>{new Date(c.created_at).toLocaleString()}</span>
                      <p className={themeClasses.textSecondary}>{c.body}</p>
                    </div>
                  ))}
                  {comments?.items?.length === 0 && (
                    <p className={`text-xs ${themeClasses.textMuted}`}>{t('admin.companyOps.tasks.noComments')}</p>
                  )}
                  {canAct(task) && (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={commentDraft}
                        onChange={(e) => setCommentDraft(e.target.value)}
                        placeholder={t('admin.companyOps.tasks.commentPlaceholder')}
                        className={`flex-1 px-3 py-1.5 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAddComment(task.id); }}
                      />
                      <button onClick={() => handleAddComment(task.id)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium ${themeClasses.buttonPrimary}`}>
                        {t('admin.companyOps.tasks.send')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ============================== Org Chart ============================== */

const emptyRoleForm = {
  name: '', title: '', description: '', responsibilities: '', category: 'executive', is_external: false, sort_order: 0,
};

const OrgChartPanel = ({ themeClasses, darkMode }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUser = useSelector((s) => s.auth?.user);
  const isAdmin = currentUser?.is_admin === true || currentUser?.role === 'admin';

  const roles = useSelector((s) => s.orgRoles.items);
  const loading = useSelector((s) => s.orgRoles.loading.items);
  const error = useSelector((s) => s.orgRoles.errors.items);
  const holdersByRole = useSelector((s) => s.orgRoles.holdersByRole);
  // create/update/delete/map/unmap all previously had no disabled guard at
  // all here, and mutate/holders errors were captured in the slice but
  // never rendered -- only the list-fetch error above ever showed anything.
  const mutating = useSelector((s) => s.orgRoles.loading.mutate);
  const mutateError = useSelector((s) => s.orgRoles.errors.mutate);
  const holdersError = useSelector((s) => s.orgRoles.errors.holders);

  const [showCreate, setShowCreate] = useState(false);
  const [newRole, setNewRole] = useState(emptyRoleForm);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyRoleForm);
  // Per-role add-holder picker state: { query, results, searching, selected }.
  // Replaces a raw numeric "User ID" input (no way to know anyone's ID
  // without looking it up elsewhere first) with search-by-username/email
  // reusing the existing admin user search (GET /admin/users?q=, already
  // admin_required-gated) -- kept in local state, not Redux, so typeahead
  // results here never collide with the separate Users tab's own paginated
  // listing state (same reasoning as NewsFeedPage's ticker search).
  const [pickerByRole, setPickerByRole] = useState({});
  const searchDebounceRef = useRef({});

  useEffect(() => {
    dispatch(fetchOrgRolesThunk());
  }, [dispatch]);

  useEffect(() => {
    roles.forEach((role) => {
      if (!holdersByRole[role.id]) dispatch(fetchOrgRoleHoldersThunk(role.id));
    });
  }, [dispatch, roles, holdersByRole]);

  const picker = (roleId) => pickerByRole[roleId] || { query: '', results: [], searching: false, selected: null };

  const setPicker = (roleId, patch) => {
    setPickerByRole((p) => ({ ...p, [roleId]: { ...picker(roleId), ...patch } }));
  };

  const handleSearchChange = (roleId, query) => {
    setPicker(roleId, { query, selected: null });
    clearTimeout(searchDebounceRef.current[roleId]);
    if (!query.trim()) {
      setPicker(roleId, { results: [], searching: false });
      return;
    }
    setPicker(roleId, { searching: true });
    searchDebounceRef.current[roleId] = setTimeout(async () => {
      try {
        const data = await AdminAPI.fetchUsersList({ q: query.trim(), limit: 8 });
        setPicker(roleId, { results: data.users || [], searching: false });
      } catch {
        setPicker(roleId, { results: [], searching: false });
      }
    }, 300);
  };

  const selectPickerUser = (roleId, user) => {
    setPicker(roleId, { selected: user, query: user.username || user.email, results: [] });
  };

  const handleCreate = (e) => {
    e.preventDefault();
    if (!newRole.name.trim() || !newRole.title.trim()) return;
    dispatch(createOrgRoleThunk({ ...newRole, sort_order: Number(newRole.sort_order) || 0 })).then((res) => {
      if (!res.error) { setNewRole(emptyRoleForm); setShowCreate(false); }
    });
  };

  const startEdit = (role) => {
    setEditingId(role.id);
    setEditForm({
      name: role.name, title: role.title, description: role.description || '',
      responsibilities: role.responsibilities || '', category: role.category,
      is_external: role.is_external, sort_order: role.sort_order,
    });
  };

  const saveEdit = (roleId) => {
    dispatch(updateOrgRoleThunk({ roleId, updates: editForm })).then((res) => {
      if (!res.error) setEditingId(null);
    });
  };

  const handleDelete = (roleId) => {
    if (window.confirm(t('admin.companyOps.orgChart.deleteConfirm'))) {
      dispatch(deleteOrgRoleThunk(roleId));
    }
  };

  const handleMap = (roleId, roleTitle) => {
    const selected = picker(roleId).selected;
    if (!selected) return;
    // Granting an org-chart role is a real privilege change (e.g. CEO),
    // not a reversible-by-accident action -- confirm who and what, matching
    // this same panel's own existing pattern for role/task deletion below.
    // The grant itself is already admin_required-gated server-side and
    // captured by AuditLogMiddleware (actor + path) -- see 08_MASTER_TODO.md.
    const label = selected.username || selected.email;
    if (!window.confirm(t('admin.companyOps.orgChart.mapConfirm', { username: label, role: roleTitle }))) return;
    dispatch(mapUserToOrgRoleThunk({ roleId, userId: selected.id })).then((res) => {
      if (!res.error) setPicker(roleId, { query: '', results: [], selected: null });
    });
  };

  const roleForm = (form, setForm, onSubmit, onCancel, submitLabel, disabled = false) => (
    <form onSubmit={onSubmit} className={`mb-4 p-4 rounded-lg border ${themeClasses.border} space-y-3`}>
      <div className="grid grid-cols-2 gap-3">
        <input type="text" placeholder={t('admin.companyOps.orgChart.namePlaceholder')} value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value.toUpperCase() }))}
          className={`px-3 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`} required />
        <input type="text" placeholder={t('admin.companyOps.orgChart.titlePlaceholder')} value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          className={`px-3 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`} required />
      </div>
      <textarea placeholder={t('admin.companyOps.orgChart.descriptionPlaceholder')} value={form.description}
        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
        className={`w-full px-3 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm resize-none`} rows={2} />
      <textarea placeholder={t('admin.companyOps.orgChart.responsibilitiesPlaceholder')} value={form.responsibilities}
        onChange={(e) => setForm((f) => ({ ...f, responsibilities: e.target.value }))}
        className={`w-full px-3 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm resize-none`} rows={3} />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-center">
        <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
          className={`px-2.5 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`}>
          {CATEGORIES.map((c) => <option key={c} value={c}>{t(`admin.companyOps.orgChart.categories.${c}`)}</option>)}
        </select>
        <input type="number" placeholder={t('admin.companyOps.orgChart.sortOrderPlaceholder')} value={form.sort_order}
          onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
          className={`px-2.5 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm`} />
        <label className={`flex items-center gap-2 text-sm ${themeClasses.textSecondary}`}>
          <input type="checkbox" checked={form.is_external}
            onChange={(e) => setForm((f) => ({ ...f, is_external: e.target.checked }))} />
          {t('admin.companyOps.orgChart.isExternal')}
        </label>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={disabled} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${themeClasses.buttonPrimary}`}>
          <Save className="h-4 w-4" /> {submitLabel}
        </button>
        <button type="button" onClick={onCancel} disabled={disabled} className={`px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${themeClasses.buttonSecondary}`}>
          {t('admin.companyOps.tasks.cancel')}
        </button>
      </div>
    </form>
  );

  return (
    <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 shadow-sm`}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className={`text-lg font-semibold ${themeClasses.text}`}>{t('admin.companyOps.orgChart.heading')}</h2>
          <p className={`text-sm ${themeClasses.textMuted} mt-0.5`}>{t('admin.companyOps.orgChart.subtitle')}</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            {(loading || mutating) && <RefreshCw className={`h-4 w-4 animate-spin ${themeClasses.textMuted}`} />}
            <button onClick={() => setShowCreate((s) => !s)} disabled={mutating}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${themeClasses.buttonPrimary}`}>
              <Plus className="h-4 w-4" /> {t('admin.companyOps.orgChart.newRole')}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className={`mb-4 flex items-center gap-2 p-3 rounded-lg ${darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700'}`}>
          <AlertTriangle className="h-4 w-4 flex-shrink-0" /> {error}
        </div>
      )}
      {(mutateError || holdersError) && (
        <div className={`mb-4 flex items-center gap-2 p-3 rounded-lg ${darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700'}`}>
          <AlertTriangle className="h-4 w-4 flex-shrink-0" /> {mutateError || holdersError}
        </div>
      )}

      {isAdmin && showCreate && roleForm(newRole, setNewRole, handleCreate, () => setShowCreate(false), t('admin.companyOps.orgChart.create'), mutating)}

      {CATEGORIES.map((category) => {
        const rolesInCategory = roles.filter((r) => r.category === category).sort((a, b) => a.sort_order - b.sort_order);
        if (rolesInCategory.length === 0) return null;
        return (
          <div key={category} className="mb-6 last:mb-0">
            <h3 className={`text-sm font-semibold uppercase tracking-wide mb-2 ${themeClasses.textMuted}`}>
              {t(`admin.companyOps.orgChart.categories.${category}`)}
            </h3>
            <div className="space-y-2">
              {rolesInCategory.map((role) => {
                const isEditing = editingId === role.id;
                const holders = holdersByRole[role.id] || [];

                if (isEditing) {
                  return (
                    <div key={role.id} className={`p-4 rounded-lg border ${themeClasses.border}`}>
                      {roleForm(editForm, setEditForm, (e) => { e.preventDefault(); saveEdit(role.id); }, () => setEditingId(null), t('admin.companyOps.tasks.save'), mutating)}
                    </div>
                  );
                }

                return (
                  <div key={role.id} className={`p-4 rounded-lg border ${themeClasses.border} ${themeClasses.hoverBg} transition-colors`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className={`font-medium ${themeClasses.text}`}>{role.title}</span>
                          {role.is_external && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                              {t('admin.companyOps.orgChart.isExternal')}
                            </span>
                          )}
                        </div>
                        {role.description && <p className={`text-sm ${themeClasses.textSecondary} mt-1`}>{role.description}</p>}
                        {role.responsibilities && (
                          <p className={`text-xs ${themeClasses.textMuted} mt-1 whitespace-pre-line`}>{role.responsibilities}</p>
                        )}
                        <div className="flex items-center gap-2 flex-wrap mt-2">
                          {holders.map((h) => (
                            <span key={h.user_id} className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                              {h.username}
                              {isAdmin && (
                                <button onClick={() => dispatch(unmapUserFromOrgRoleThunk({ roleId: role.id, userId: h.user_id }))}
                                  disabled={mutating}
                                  className="disabled:opacity-50 disabled:cursor-not-allowed"
                                  aria-label={t('admin.companyOps.orgChart.removeHolderAria', { username: h.username })}>
                                  <X className="h-3 w-3" />
                                </button>
                              )}
                            </span>
                          ))}
                          {isAdmin && (
                            <div className="relative flex items-center gap-1">
                              <div className="relative">
                                <Search className={`absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 pointer-events-none ${themeClasses.textMuted}`} />
                                <input
                                  type="text"
                                  placeholder={t('admin.companyOps.orgChart.userSearchPlaceholder')}
                                  value={picker(role.id).query}
                                  onChange={(e) => handleSearchChange(role.id, e.target.value)}
                                  disabled={mutating}
                                  className={`w-40 pl-6 pr-2 py-1 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-xs disabled:opacity-50`}
                                />
                                {(picker(role.id).searching || picker(role.id).results.length > 0) && (
                                  <div className={`absolute z-10 top-full left-0 mt-1 w-56 rounded-lg border shadow-lg overflow-hidden ${themeClasses.border} ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                                    {picker(role.id).searching && (
                                      <div className={`px-3 py-2 text-xs ${themeClasses.textMuted}`}>{t('admin.companyOps.orgChart.searching')}</div>
                                    )}
                                    {!picker(role.id).searching && picker(role.id).results.map((u) => (
                                      <button
                                        type="button"
                                        key={u.id}
                                        onMouseDown={() => selectPickerUser(role.id, u)}
                                        className={`w-full text-left px-3 py-1.5 text-xs ${themeClasses.hoverBg}`}
                                      >
                                        <div className={themeClasses.text}>{u.username}</div>
                                        <div className={themeClasses.textMuted}>{u.email}</div>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={() => handleMap(role.id, role.title)}
                                disabled={mutating || !picker(role.id).selected}
                                aria-label={t('admin.companyOps.orgChart.addHolderAria')}
                                className={`p-1 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${darkMode ? 'text-gray-500 hover:text-blue-400 hover:bg-blue-900/20' : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'}`}
                              >
                                <UserPlus className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      {isAdmin && (
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button onClick={() => startEdit(role)} disabled={mutating} aria-label={t('admin.companyOps.tasks.editAria', { title: role.title })}
                            className={`p-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${darkMode ? 'text-gray-500 hover:text-blue-400 hover:bg-blue-900/20' : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'}`}>
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button onClick={() => handleDelete(role.id)} disabled={mutating} aria-label={t('admin.companyOps.tasks.deleteAria', { title: role.title })}
                            className={`p-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${darkMode ? 'text-gray-500 hover:text-red-400 hover:bg-red-900/20' : 'text-gray-400 hover:text-red-600 hover:bg-red-50'}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AdminCompanyOpsComponent;
